import { useEffect, useRef } from 'react'
import { kernel } from '@omni/kernel'
import { ListingPage } from './manager/ListingPage'
import { SettingsPage } from './manager/SettingsPage'
import { BoardPage } from './shell/BoardPage'
import { watchBoardReady } from './shell/board-ready'
import { OffHint } from './shell/OffHint'
import { CANVAS_ID, navigate, parseRoute, usePath } from './shell/router'

export function App() {
  const path = usePath()
  const route = parseRoute(path)

  // '/' always shows the one canvas (G4: no board list).
  useEffect(() => {
    if (route.page === 'home') navigate(`/v/${CANVAS_ID}`, { replace: true })
  }, [route.page])

  // the one routing concession the host makes: expose the current view.
  // Plugins bind to one view (document, sync); moving to a DIFFERENT view
  // restarts them so they rebind cleanly — after the new service is in place.
  const lastBoundView = useRef('__unset__')
  // The plugins start on whatever page loads first. Loaded on /settings they
  // start with no omni.boards, so the document binds to no view and nothing
  // can open a board; "← Board" then never restarted them, since no view had
  // been bound yet ("Create a new board" did nothing, 2026-09-26 new-user
  // test). The first board page after starting elsewhere restarts them.
  const startedElsewhere = useRef(null)
  if (startedElsewhere.current === null) startedElsewhere.current = route.page !== 'view' && route.page !== 'home'
  useEffect(() => {
    if (route.page !== 'view' && route.page !== 'home') return
    if (route.page === 'home') return // about to go to /v/canvas; binding null here restarted every plugin twice and killed running AI jobs (2026-10-09)
    const viewId = route.page === 'view' ? route.viewId : null
    const d = kernel.services.provide('omni.boards', {
      current: () => ({ boardId: viewId }),
      open: (id) => navigate(`/v/${id}`),
      openPage: (page) => navigate(`/${page}`), // 'plugins' | 'settings', from the main menu
      shareUrl: () => location.href,
    })
    if (lastBoundView.current !== '__unset__' && lastBoundView.current !== viewId) {
      const before = {
        'omni.camera': kernel.services.getNow('omni.camera'),
        'omni.document': kernel.services.getNow('omni.document'),
      }
      kernel.restart()
      watchBoardReady({
        getService: (id) => kernel.services.getNow(id),
        before,
        onReady: ({ timedOut }) => {
          window.dispatchEvent(new CustomEvent('omni:board-ready', { detail: { boardId: viewId, timedOut } }))
          kernel.services.getNow('omni.curtain')?.release(150)
        },
      })
    } else if (lastBoundView.current === '__unset__' && startedElsewhere.current) {
      startedElsewhere.current = false
      kernel.restart()
    }
    lastBoundView.current = viewId
    return () => d.dispose()
  }, [route.page, route.viewId])

  if (route.page === 'plugins') return <ListingPage />
  if (route.page === 'settings') return <SettingsPage />
  if (route.page === 'view')
    return (
      <>
        <BoardPage boardId={route.viewId} />
        <OffHint />
      </>
    )
  return null
}
